import { useState } from 'react';
import { Auth } from './Auth';
import overviewShot from '../assets/screens/overview.png';
import exploreShot from '../assets/screens/explore.png';
import forecastShot from '../assets/screens/forecast.png';
import aiShot from '../assets/screens/ai.png';
import './landing.css';

/**
 * Public landing page — shown before authentication.
 *
 * This is the first thing visitors see. It must answer three questions in five
 * seconds: what is this, who is it for, and why should I care. The design
 * prioritises clarity over cleverness, and every section earns its place.
 *
 * All styling lives in landing.css as .lp-* classes mapped to the design
 * tokens in theme.css, so the page follows the app theme (dark mode included).
 */
export function LandingPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');

  if (showAuth) {
    return (
      <Auth
        onSignedIn={onSignedIn}
        initialMode={authMode}
        onBack={() => setShowAuth(false)}
      />
    );
  }

  return (
    <div className="lp-root">
      <LandingNav onSignUp={() => { setAuthMode('signup'); setShowAuth(true); }} onLogin={() => { setAuthMode('login'); setShowAuth(true); }} />
      <Hero onGetStarted={() => { setAuthMode('signup'); setShowAuth(true); }} />
      <LogoBar />
      <HowItWorksSection />
      <Gallery />
      <FeaturesGrid onGetStarted={() => { setAuthMode('signup'); setShowAuth(true); }} />
      <UseCases />
      <Comparison />
      <Faq />
      <FinalCta onGetStarted={() => { setAuthMode('signup'); setShowAuth(true); }} />
      <Footer />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Navigation
// -----------------------------------------------------------------------------

function LandingNav({ onSignUp, onLogin, transparent }: { onSignUp: () => void; onLogin: () => void; transparent?: boolean }) {
  return (
    <nav className={transparent ? 'lp-nav lp-nav--transparent' : 'lp-nav'}>
      <div className="lp-nav-brand">
        <div className="lp-mark">
          <span>D</span>
        </div>
        <span className="lp-nav-name">DataVantage</span>
      </div>
      <div className="lp-nav-links">
        <a href="#how-it-works" className="lp-nav-link">How it works</a>
        <a href="#features" className="lp-nav-link">Features</a>
        <button onClick={onLogin} className="lp-btn lp-btn--outline lp-btn--nav">Sign in</button>
        <button onClick={onSignUp} className="lp-btn lp-btn--primary lp-btn--nav">Get started free</button>
      </div>
    </nav>
  );
}

// -----------------------------------------------------------------------------
// Hero
// -----------------------------------------------------------------------------

function Hero({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="lp-hero">
      <div className="lp-hero-pill">
        <span className="lp-hero-pill-badge">NEW</span>
        Template marketplace, Power BI export & client portals are here
      </div>

      <h1 className="lp-hero-title">
        Upload a spreadsheet.<br />
        <span className="lp-hero-gradient">Get a dashboard in 60 seconds.</span>
      </h1>

      <p className="lp-hero-sub">
        Every figure computed, traceable, and exportable. Connect your data sources, get AI-powered
        insights, forecast trends, and deliver branded reports to clients — all without writing a formula.
      </p>

      <div className="lp-hero-actions">
        <button onClick={onGetStarted} className="lp-btn lp-btn--primary lp-btn--lg lp-btn--shadow">
          Start for free →
        </button>
        <a href="#how-it-works" className="lp-btn lp-btn--outline lp-btn--lg">
          See how it works
        </a>
      </div>

      <p className="lp-hero-note">Works with CSV, Excel, JSON, PDF</p>
      <a href="#gallery" className="lp-hero-cue">See the real app ↓</a>

      {/* Dashboard preview mockup — illustrative layout only. The figures are
          sample data, and the chip says so: the brand promise is that nothing
          in the product is invented, so the marketing page must not invent
          either. */}
      <div className="lp-mock">
        <div className="lp-mock-inner">
          {/* Mock toolbar */}
          <div className="lp-mock-toolbar">
            <div className="lp-mock-dot lp-mock-dot--red" />
            <div className="lp-mock-dot lp-mock-dot--amber" />
            <div className="lp-mock-dot lp-mock-dot--green" />
            <span className="lp-mock-chip">Sample data</span>
          </div>
          {/* Mock scorecards */}
          <div className="lp-mock-scorecards">
            {[
              { label: 'Revenue', value: '$284,520', change: '+12.5%', up: true },
              { label: 'Orders', value: '3,847', change: '+8.2%', up: true },
              { label: 'Avg Order', value: '$73.94', change: '-2.1%', up: false },
              { label: 'Conversion', value: '3.4%', change: '+0.6%', up: true },
            ].map((card) => (
              <div key={card.label} className="lp-mock-card">
                <div className="lp-mock-label">{card.label}</div>
                <div className="lp-mock-value">{card.value}</div>
                <div className={card.up ? 'lp-mock-change lp-mock-change--up' : 'lp-mock-change lp-mock-change--down'}>{card.change}</div>
              </div>
            ))}
          </div>
          {/* Mock chart area */}
          <div className="lp-mock-charts">
            <div className="lp-mock-chart">
              <div className="lp-mock-chart-title">Revenue over time</div>
              <svg viewBox="0 0 400 100" className="lp-mock-chart-svg">
                <defs>
                  <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--series-1)" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="var(--series-1)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0,80 C40,75 80,60 120,55 C160,50 200,65 240,40 C280,15 320,25 360,10 L400,5 L400,100 L0,100 Z" fill="url(#heroGrad)" />
                <path d="M0,80 C40,75 80,60 120,55 C160,50 200,65 240,40 C280,15 320,25 360,10 L400,5" fill="none" stroke="var(--series-1)" strokeWidth="2.5" />
              </svg>
            </div>
            <div className="lp-mock-chart">
              <div className="lp-mock-chart-title">By channel</div>
              <div className="lp-mock-bars">
                {[
                  { label: 'Organic', pct: 62, tone: 'blue' },
                  { label: 'Paid', pct: 24, tone: 'violet' },
                  { label: 'Referral', pct: 14, tone: 'green' },
                ].map((bar) => (
                  <div key={bar.label} className="lp-mock-bar">
                    <span className="lp-mock-bar-label">{bar.label}</span>
                    <div className="lp-mock-bar-track">
                      <div className="lp-mock-bar-fill" data-tone={bar.tone} data-pct={bar.pct} />
                    </div>
                    <span className="lp-mock-bar-pct">{bar.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Logo bar
// -----------------------------------------------------------------------------

function LogoBar() {
  const logos = ['SEO Agencies', 'E-commerce', 'SaaS Startups', 'Marketing Teams', 'Consultants', 'Enterprise'];
  return (
    <section className="lp-logos">
      <p className="lp-logos-title">
        Trusted by data teams at
      </p>
      <div className="lp-logos-row">
        {logos.map((name) => (
          <span key={name} className="lp-logo">{name}</span>
        ))}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// How it works
// -----------------------------------------------------------------------------

function HowItWorksSection() {
  const steps = [
    { n: 1, icon: '⇥', title: 'Connect your data', desc: 'Drop a CSV, Excel, JSON, or PDF — or connect Google Search Console, BigQuery, Sheets, or Meta Ads. One upload, zero config.' },
    { n: 2, icon: '◈', title: 'It reads everything', desc: 'Every column is typed, IDs are excluded from metrics, gaps are counted. It works out what your data is about before you do.' },
    { n: 3, icon: '✦', title: 'Dashboard appears', desc: 'Scorecards, findings, chart recommendations, and a written summary — all computed from your data, nothing invented.' },
    { n: 4, icon: '⤳', title: 'Dig deeper', desc: 'Group, filter, pivot, forecast. Every interaction re-queries the same data, so two charts can never disagree about the same number.' },
    { n: 5, icon: '↗', title: 'Deliver it', desc: 'Export to PDF, push to Looker Studio or Power BI, send branded client reports, or schedule it to refresh and email itself.' },
  ];

  return (
    <section id="how-it-works" className="lp-section">
      <div className="lp-section-inner">
        <div className="lp-section-head">
          <span className="lp-eyebrow">How it works</span>
          <h2 className="lp-h2">Five steps. Two minutes. Done.</h2>
          <p className="lp-lede">
            From raw data to a finished dashboard you can send to a client. No formulas, no config, no guessing.
          </p>
        </div>

        <div className="lp-steps">
          {steps.map((step) => (
            <div key={step.n} className="lp-step">
              <div className="lp-step-icon">
                {step.icon}
              </div>
              <div>
                <div className="lp-step-head">
                  <span className="lp-step-badge">Step {step.n}</span>
                  <h3 className="lp-step-title">{step.title}</h3>
                </div>
                <p className="lp-step-desc">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Gallery — real app screenshots
// -----------------------------------------------------------------------------

function Gallery() {
  const shots = [
    {
      id: 'overview',
      label: 'Dashboard',
      src: overviewShot,
      alt: 'Dashboard overview with scorecards and charts computed from uploaded data',
    },
    {
      id: 'explore',
      label: 'Explore your data',
      src: exploreShot,
      alt: 'Explore view with grouped chart and table controls',
    },
    {
      id: 'forecast',
      label: 'Forecasting',
      src: forecastShot,
      alt: 'Forecasting with trend and seasonality model and projection band',
    },
    {
      id: 'ai',
      label: 'AI insights',
      src: aiShot,
      alt: 'AI analytics page with auto-analysed blocks and highlight cards',
    },
  ];
  const [activeId, setActiveId] = useState(shots[0]!.id);
  const current = shots.find((s) => s.id === activeId) ?? shots[0]!;

  return (
    <section id="gallery" className="lp-section">
      <div className="lp-section-inner">
        <div className="lp-section-head">
          <span className="lp-eyebrow">See it in action</span>
          <h2 className="lp-h2">The real app, not mockups</h2>
          <p className="lp-lede">
            Every screen below is the actual product, running on sample data — the same computation, the same honesty badges you would see after signing up.
          </p>
        </div>

        <div className="lp-gallery-tabs" role="tablist" aria-label="App screenshots">
          {shots.map((shot) => (
            <button
              key={shot.id}
              role="tab"
              aria-selected={shot.id === activeId}
              className={shot.id === activeId ? 'lp-gallery-tab lp-gallery-tab--active' : 'lp-gallery-tab'}
              onClick={() => setActiveId(shot.id)}
            >
              {shot.label}
            </button>
          ))}
        </div>

        <div className="lp-shot" role="tabpanel" aria-label={current.label}>
          <img
            key={current.id}
            className="lp-shot-img"
            src={current.src}
            alt={current.alt}
            loading="lazy"
          />
          <span className="lp-shot-badge">Real app — sample data</span>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Features grid
// -----------------------------------------------------------------------------

function FeaturesGrid({ onGetStarted }: { onGetStarted: () => void }) {
  const features = [
    { icon: '✦', title: 'AI Analytics', desc: 'Upload a file, get the whole dashboard back. It decides what the data is about and builds around that.', tone: 'blue' },
    { icon: '⊞', title: 'Template Marketplace', desc: 'Pre-built dashboards for e-commerce, SaaS, SEO, real estate, and more. One click to a finished report.', tone: 'violet' },
    { icon: '⚡', title: 'Power BI Integration', desc: 'Push data to your workspace, export templates with DAX measures, or download an optimised CSV.', tone: 'amber' },
    { icon: '⤳', title: 'Forecasting', desc: 'Decomposable time-series with detected seasonality, changepoints, and honest backtesting.', tone: 'green' },
    { icon: '⎔', title: 'Client Portals', desc: 'Branded portals where clients view reports at their own URL. Your logo, your colours, invisible to them.', tone: 'red' },
    { icon: '✉', title: 'Client Reports', desc: 'Branded PDF reports with executive summary, scorecards, and findings. Composed and emailed automatically.', tone: 'cyan' },
    { icon: '↗', title: 'Looker Studio', desc: 'Publish to Google Sheets, get a one-click Looker Studio report with every field typed correctly.', tone: 'blue' },
    { icon: '⟳', title: 'Scheduled Refresh', desc: 'Set a source to refresh daily. It emails you only when something moved — silence is a signal.', tone: 'violet' },
    { icon: '▦', title: 'Pivot & Explore', desc: 'Cross-tabulate any two dimensions. Empty cells show a dash, never a zero. Margins are honest.', tone: 'amber' },
  ];

  return (
    <section id="features" className="lp-section lp-section--alt">
      <div className="lp-section-inner">
        <div className="lp-section-head">
          <span className="lp-eyebrow">Features</span>
          <h2 className="lp-h2">Everything you need, nothing you don't</h2>
          <p className="lp-lede">
            Every feature is built on real computation. Nothing is sample data, a placeholder, or a guess.
          </p>
        </div>

        <div className="lp-grid">
          {features.map((f) => (
            <div key={f.title} className={`lp-card lp-tone--${f.tone}`}>
              <div className="lp-card-icon">{f.icon}</div>
              <h3 className="lp-card-title">{f.title}</h3>
              <p className="lp-card-desc">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="lp-cta">
          <button onClick={onGetStarted} className="lp-btn lp-btn--primary lp-btn--lg">
            Try it free →
          </button>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Use cases
// -----------------------------------------------------------------------------

function UseCases() {
  // Illustrative workflows: the steps describe real features, but the
  // outcomes are examples, not measured results — no invented timings.
  const cases = [
    {
      who: 'SEO Agency',
      scenario: 'Monday morning. Client traffic dipped 15%.',
      outcome: 'Connected Search Console → Overview flags which queries lost clicks → Set a weekly schedule → Find the tracking outage in minutes, not days.',
      metric: 'Minutes', metricLabel: 'to diagnosis',
    },
    {
      who: 'E-commerce Brand',
      scenario: 'Month end. Need to explain margin to the board.',
      outcome: 'Upload your order rows → IDs auto-excluded → Forecast shows the trend against target → Board deck exported as a branded PDF.',
      metric: 'Minutes', metricLabel: 'to a board-ready deck',
    },
    {
      who: 'Marketing Agency',
      scenario: '12 clients, each needing a weekly performance report.',
      outcome: 'Created 12 client portals → Branded each with client logo → Scheduled Meta Ads refresh → Clients get auto-emailed reports every Monday at 6am.',
      metric: 'Zero', metricLabel: 'hours of weekly manual work',
    },
  ];

  return (
    <section className="lp-section">
      <div className="lp-section-inner">
        <div className="lp-section-head">
          <span className="lp-eyebrow">Use cases</span>
          <h2 className="lp-h2">Real workflows, real time saved</h2>
          <p className="lp-note">Illustrative scenarios — the steps are real features; the outcomes are examples.</p>
        </div>

        <div className="lp-cases">
          {cases.map((c) => (
            <div key={c.who} className="lp-case">
              <div>
                <span className="lp-case-who">{c.who}</span>
                <h3 className="lp-case-title">{c.scenario}</h3>
                <p className="lp-case-desc">{c.outcome}</p>
              </div>
              <div className="lp-case-metric">
                <div className="lp-case-metric-value">{c.metric}</div>
                <div className="lp-case-metric-label">{c.metricLabel}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Comparison
// -----------------------------------------------------------------------------

function Comparison() {
  const rows = [
    { feature: 'Upload → dashboard in < 60s', us: true, them: false },
    { feature: 'Every figure is traceable to source data', us: true, them: false },
    { feature: 'AI insights with evidence guardrails', us: true, them: 'partial' },
    { feature: 'Looker Studio one-click export', us: true, them: true },
    { feature: 'Power BI push + template export', us: true, them: false },
    { feature: 'White-label client portals', us: true, them: false },
    { feature: 'Automated branded client reports', us: true, them: false },
    { feature: 'Forecasting with backtesting', us: true, them: 'partial' },
    { feature: 'Self-hosted / open source', us: true, them: false },
  ];

  return (
    <section className="lp-section lp-section--alt">
      <div className="lp-compare-wrap">
        <div className="lp-section-head">
          <h2 className="lp-h2">Why DataVantage</h2>
          <p className="lp-lede">Compared to typical BI tools and reporting platforms</p>
        </div>

        <div className="lp-compare">
          <div className="lp-compare-head">
            <span></span>
            <span className="lp-compare-us">DataVantage</span>
            <span className="lp-compare-them">Others</span>
          </div>
          {rows.map((row) => (
            <div key={row.feature} className="lp-compare-row">
              <span>{row.feature}</span>
              <span className="lp-compare-us">{row.us === true ? '✓' : '—'}</span>
              <span className="lp-compare-them">
                {row.them === true ? '✓' : row.them === 'partial' ? '~' : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// FAQ
// -----------------------------------------------------------------------------

function Faq() {
  const [open, setOpen] = useState<number | null>(null);
  const items = [
    { q: 'Do I need to know SQL or formulas?', a: 'No. Upload a file and the dashboard appears. Every computation is done for you. If you want to dig deeper, the Explore and Pivot tabs let you group, filter, and aggregate without writing anything.' },
    { q: 'What file formats are supported?', a: 'CSV, TSV, XLSX, JSON, NDJSON, XML, PDF, and plain-text logs. PDF and log files are flagged as "structure inferred" so you know the table layout was guessed, not read.' },
    { q: 'Is my data safe?', a: 'Data stays in your workspace and is never shared. Sessions use hashed tokens, credentials are AES-256-GCM encrypted, and tenancy is enforced at the query level. You can self-host the entire platform.' },
    { q: 'Can I white-label for my clients?', a: 'Yes. Set your company name, logo, accent colour, and footer. Remove "Powered by DataVantage". Create branded client portals where clients view reports at their own URL.' },
    { q: 'What happens when I hit the upload limit?', a: 'You keep access to everything you have already created. You just cannot upload new datasets until the next month. We will never delete your data.' },
    { q: 'Can I connect live data sources?', a: 'Yes. Google Search Console, BigQuery, Google Sheets, and Meta Ads are supported. Data refreshes on a schedule you set, and you get emailed only when something material changes.' },
  ];

  return (
    <section className="lp-section lp-section--alt">
      <div className="lp-faq-wrap">
        <h2 className="lp-faq-title">Frequently asked questions</h2>
        <div className="lp-faq">
          {items.map((item, i) => (
            <div key={i} className={open === i ? 'lp-faq-item lp-open' : 'lp-faq-item'}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="lp-faq-q"
              >
                {item.q}
                <span className="lp-faq-mark">+</span>
              </button>
              {open === i && (
                <p className="lp-faq-a">{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Final CTA
// -----------------------------------------------------------------------------

function FinalCta({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="lp-final">
      <div className="lp-final-inner">
        <h2 className="lp-final-title">Stop building dashboards by hand.</h2>
        <p className="lp-final-sub">
          Upload your first file. In 60 seconds you will have a dashboard you can send to a client.
        </p>
        <button onClick={onGetStarted} className="lp-btn lp-btn--primary lp-btn--shadow-lg">
          Get started free →
        </button>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Footer
// -----------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="lp-footer">
      <div className="lp-footer-brand">
        <div className="lp-footer-mark">
          <span>D</span>
        </div>
        <span className="lp-footer-name">DataVantage</span>
      </div>
      <p className="lp-footer-tag">
        Every figure computed. Every number traceable. Nothing invented.
      </p>
    </footer>
  );
}
